import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollReveal — a single, site-wide "rise + fade" reveal as content
 * scrolls into view. Ported from the new theme's section-two choreography
 * (autothinklogicflow): the same subtle motion — a small upward translate
 * dissolving to rest on `cubic-bezier(.22,1,.36,1)` — generalized into one
 * reusable observer instead of the theme's bespoke per-element keyframes.
 *
 * How it stays safe:
 *   - Pure progressive enhancement. The hidden state is gated on
 *     `html.reveal-on`, which this component adds only at runtime once the
 *     observer is live. No JS (or a crawler reading the prerendered HTML) →
 *     nothing is ever hidden. Content lives in the DOM the whole time
 *     (opacity/transform only), so SEO/AEO/GEO are untouched.
 *   - `prefers-reduced-motion` short-circuits before anything is tagged, so
 *     those visitors get the full page with no motion.
 *   - Blocks already in view on load are shown instantly (no first-paint
 *     flash); only blocks below the fold animate in as you scroll to them.
 *
 * The mounted component just (re)tags on every route change; the observer
 * and tagging pass are module-level and idempotent.
 */

// Recurring section-heading + card/panel building blocks shared across the
// themed shells. `.aa-center` alone covers almost every section heading on
// the service/explore/content pages; the rest are the card grids and panels.
const SELECTORS = [
  '.aa-center',
  '.ht-services-head',
  '.ht-process-head',
  '.ht-card',
  '.ht-need',
  '.ht-svc-card',
  '.aa-svc-card',
  '.wd-price-card',
  '.wd-inc-grid > li',
  '.wd-step',
  '.ia-fit-card',
  '.ia-lead-card',
  '.ia-leadmag',
  '.st-post',
  '.st-card',
  '.bl-card',
  '.ab-prod',
  '.ab-work-card',
  '.tl-card',
  '.tk-card',
  '.tk-form-card',
  '.tk-stat',
  '.ht-step',
  '.ht-minicard',
  '.ht-panel',
  '.need',
].join(',');

let observer: IntersectionObserver | null = null;

function ensureObserver(): IntersectionObserver | null {
  if (typeof IntersectionObserver === 'undefined') return null;
  if (observer) return observer;
  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-in');
        observer?.unobserve(entry.target);
      }
    },
    // Reveal slightly before the block is fully in view so it settles into
    // place rather than popping at the very edge.
    { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
  );
  return observer;
}

/** Tag any not-yet-tagged reveal targets and observe the below-fold ones. */
function tagReveals(): void {
  const io = ensureObserver();
  if (!io) return;
  const vh = window.innerHeight || document.documentElement.clientHeight || 0;
  const nodes = document.querySelectorAll<HTMLElement>(SELECTORS);
  nodes.forEach((el) => {
    if (el.dataset.reveal !== undefined) return; // already tagged
    if (el.closest('[data-no-reveal]')) return; // opt-out escape hatch

    // Stagger siblings that share a parent (cards in a row) so a grid
    // cascades in instead of arriving as one block.
    let idx = 0;
    for (let sib = el.previousElementSibling; sib; sib = sib.previousElementSibling) {
      if ((sib as HTMLElement).matches?.(SELECTORS)) idx += 1;
    }
    if (idx > 0) el.style.setProperty('--reveal-d', `${Math.min(idx, 6) * 70}ms`);

    el.dataset.reveal = '';

    // Already visible on this pass → show instantly (no load flash). Only
    // blocks below the current viewport animate in when scrolled to.
    if (el.getBoundingClientRect().top < vh * 0.92) {
      el.classList.add('is-in');
    } else {
      io.observe(el);
    }
  });
}

export const ScrollReveal = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    document.documentElement.classList.add('reveal-on');

    // Several passes: route content is lazy-loaded, so it can mount a few
    // frames after the location changes. tagReveals is idempotent, so the
    // repeated passes only pick up newly mounted blocks.
    const timers = [0, 120, 400, 900].map((d) => window.setTimeout(tagReveals, d));
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [pathname]);

  return null;
};
